import React, { useEffect, useState, useMemo } from "react";
import {
  useNavigate,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import GlobalLoader from "../common/GlobalLoader";
import InstitutionCourseCard from "../admin/institutionCourseCard";
import EmptyPage from "../common/emptyPage";
import Cookies from "js-cookie";
import "../../styles/institution/institutionBatchCourseList.scss";
import { getAllInstitutionCourse } from "../../helper/apicalls/institution";
import { getBoards, getClassNames } from "../../helper/apicalls/course";

const getOptionName = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  if (typeof item === "object") {
    return String(item.name || item.board || item.class_name || "").trim();
  }
  return "";
};

const getCourseBoardName = (course) => {
  const raw =
    course?.board?.name ||
    course?.board?.board ||
    course?.board ||
    "";
  return typeof raw === "string" ? raw.trim() : "";
};

const getCourseClassName = (course) => {
  const raw =
    course?.class_name?.name ||
    course?.class_name ||
    course?.class?.name ||
    course?.class ||
    "";
  return typeof raw === "string" ? raw.trim() : "";
};

const InstitutionBatchCourseList = () => {
  const authToken = Cookies.get("institution_token") || Cookies.get("admin_token");
  const {
    setHeaderName,
    setIsShowSearch,
    searchQuery,
    setHeaderExtra,
  } = useOutletContext() || {};
  const navigate = useNavigate();
  const location = useLocation();

  const [institutionBatchCourseData, setInstitutionBatchCourseData] = useState([]);
  const [loader, setLoader] = useState(false);

  // Filter options & selections
  const [boardOptions, setBoardOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [courseBoards, setCourseBoards] = useState([]);
  const [courseClasses, setCourseClasses] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedClass, setSelectedClass] = useState("");

  // Debounced search and filters
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedBoard, setDebouncedBoard] = useState("");
  const [debouncedClass, setDebouncedClass] = useState("");

  const handleCourseClick = (courseId) => {
    const isTeacherHomework = location.pathname.startsWith("/teacher/homework");
    if (isTeacherHomework) {
      navigate(`/teacher/homework/batches?course=${courseId?.key}`);
      return;
    }
    if (location.pathname.startsWith("/teacher/batches")) {
      navigate(`/teacher/batches/info?course=${courseId?.key}`);
      return;
    }
    navigate(`/institution/batches/info?course=${courseId?.key}`);
  };

  const loadFilterOptions = async () => {
    try {
      const [boardsRes, classesRes] = await Promise.all([
        getBoards(true),
        getClassNames(),
      ]);
      if (Array.isArray(boardsRes)) {
        const boardNames = boardsRes.map(getOptionName).filter(Boolean);
        setBoardOptions(boardNames);
      }
      if (Array.isArray(classesRes)) {
        const classNames = classesRes.map(getOptionName).filter(Boolean);
        setClassOptions(classNames);
      }
    } catch (err) {
      console.error("Error loading board/class filter options:", err);
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const fetchInstitutionBatchCourse = async (
    search = "",
    board = "",
    className = ""
  ) => {
    setLoader(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (board.trim()) params.board = board.trim();
      if (className.trim()) params.class_name = className.trim();
      const res = await getAllInstitutionCourse(authToken, undefined, params);
      const courses = res[0]?.course || [];
      setInstitutionBatchCourseData(courses);

      // Cache all boards and classes discovered across courses so dropdown options don't disappear on filter
      if (!board && !className) {
        const foundBoards = courses.map(getCourseBoardName).filter(Boolean);
        const foundClasses = courses.map(getCourseClassName).filter(Boolean);
        if (foundBoards.length > 0) {
          setCourseBoards((prev) => Array.from(new Set([...prev, ...foundBoards])));
        }
        if (foundClasses.length > 0) {
          setCourseClasses((prev) => Array.from(new Set([...prev, ...foundClasses])));
        }
      }
    } catch (err) {
      console.error("Failed to fetch institution courses:", err);
      setInstitutionBatchCourseData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery || "");
      setDebouncedBoard(selectedBoard || "");
      setDebouncedClass(selectedClass || "");
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, selectedBoard, selectedClass]);

  useEffect(() => {
    fetchInstitutionBatchCourse(debouncedSearch, debouncedBoard, debouncedClass);
  }, [debouncedSearch, debouncedBoard, debouncedClass, location.pathname]);

  const uniqueBoards = useMemo(
    () =>
      Array.from(
        new Set([
          ...boardOptions,
          ...courseBoards,
          ...institutionBatchCourseData.map(getCourseBoardName).filter(Boolean),
        ])
      ).sort((a, b) => a.localeCompare(b)),
    [boardOptions, courseBoards, institutionBatchCourseData]
  );

  const uniqueClasses = useMemo(
    () =>
      Array.from(
        new Set([
          ...classOptions,
          ...courseClasses,
          ...institutionBatchCourseData.map(getCourseClassName).filter(Boolean),
        ])
      ).sort((a, b) => a.localeCompare(b)),
    [classOptions, courseClasses, institutionBatchCourseData]
  );

  // Client-side filtering ensures immediate responsive updates
  const filteredCourses = useMemo(() => {
    return (institutionBatchCourseData || []).filter((course) => {
      if (selectedBoard) {
        const bName = getCourseBoardName(course).trim().toLowerCase();
        const sBoard = selectedBoard.trim().toLowerCase();
        if (bName !== sBoard) {
          return false;
        }
      }
      if (selectedClass) {
        const cName = getCourseClassName(course).trim().toLowerCase();
        const sClass = selectedClass.trim().toLowerCase();
        if (cName !== sClass) {
          return false;
        }
      }
      if (debouncedSearch) {
        const sText = debouncedSearch.trim().toLowerCase();
        const courseName = (course?.name || course?.title || "").toLowerCase();
        if (!courseName.includes(sText)) {
          return false;
        }
      }
      return true;
    });
  }, [institutionBatchCourseData, selectedBoard, selectedClass, debouncedSearch]);

  useEffect(() => {
    if (setHeaderName) {
      setHeaderName(
        location.pathname.startsWith("/teacher/homework")
          ? "Homework"
          : "Batch Courses"
      );
    }
    if (setIsShowSearch) {
      setIsShowSearch(true);
    }

    return () => {
      if (setHeaderExtra) setHeaderExtra(null);
    };
  }, [setHeaderName, setIsShowSearch, setHeaderExtra, location.pathname]);

  useEffect(() => {
    if (!setHeaderExtra) return;
    if (uniqueBoards.length === 0 && uniqueClasses.length === 0) {
      setHeaderExtra(null);
      return;
    }

    setHeaderExtra(
      <div className="header-filters-wrapper">
        {uniqueBoards.length > 0 && (
          <div className="header-filter-group">
            <select
              id="header-board-filter"
              className={`header-filter-select board-filter ${selectedBoard ? "active" : ""}`}
              aria-label="Filter courses by board"
              value={selectedBoard}
              onChange={(e) => setSelectedBoard(e.target.value)}
            >
              <option value="">All Board</option>
              {uniqueBoards.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        )}

        {uniqueClasses.length > 0 && (
          <div className="header-filter-group">
            <select
              id="header-class-filter"
              className={`header-filter-select class-filter ${selectedClass ? "active" : ""}`}
              aria-label="Filter courses by class"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {uniqueClasses.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    );

    return () => {
      setHeaderExtra(null);
    };
  }, [
    setHeaderExtra,
    selectedBoard,
    selectedClass,
    uniqueBoards,
    uniqueClasses,
  ]);

  return (
    <div className="institution-batch-course-container">
      {loader && <GlobalLoader visible={loader} text="Loading courses..." />}
      <div className="institution-batch-course-header">
        <h4 className="section-title">Courses List</h4>
      </div>
      {Array.isArray(filteredCourses) && filteredCourses.length > 0 ? (
        <div className="institution-batch-course-grid">
          {filteredCourses.map((course) => (
            <InstitutionCourseCard
              key={course.id || course.key}
              course={course}
              onClick={handleCourseClick}
              hideMenuIcon={true}
            />
          ))}
        </div>
      ) : (
        !loader && <EmptyPage content={"No courses Available"} />
      )}
    </div>
  );
};

export default InstitutionBatchCourseList;
